import {
  ContentBlock,
  DecisionStep,
  FactoryTypes,
  GuideContent,
  GuideContentWrapped,
  GuideTypes,
  IntroHeader,
  Metadata,
  WizardButton,
  WizardButtonType,
  WizardContainer,
  WizardStep,
  WizardStepContainer,
  WizardStepType,
} from "../models";
import { Entry } from "contentful";
import { convertToHtmlString, findContent, parseIntoImage, extractContent } from "../utils";
import { FieldMapping, UserContextValues } from "../types";
import { getFactoryByType } from "./factoriesByType";
import { getDecisionStepCount } from "../hooks";

export const createWizardContainer = (
  fieldMapping: FieldMapping<WizardContainer>,
  entries: Entry<ContentBlock & Metadata>[],
  guideType: GuideTypes,
  userContextValues?: UserContextValues,
): WizardContainer => {
  type StepInnerType = {
    type: FactoryTypes;
    fieldMapping: FieldMapping<GuideContent>;
  };

  type WizardStepWrapper = {
    introHeaderFieldMapping: FieldMapping<IntroHeader>;
    wizardStepFieldMapping: FieldMapping<WizardStep>;
    primaryButtonFieldMapping?: FieldMapping<WizardButton>;
    secondaryButtonFieldMapping?: FieldMapping<WizardButton>;
    content: StepInnerType[];
  }

  // Parse each step from the field mapping into 
  // a valid wizard step and push it into the array
  const steps: WizardStepContainer[] = [];
  (fieldMapping.steps as unknown as WizardStepWrapper[])?.forEach((step) => {
    // Get the intro header content
    const stepTitle = findContent(
      entries,
      step.introHeaderFieldMapping.title,
      guideType
    );

    const stepSubtitle = findContent(
      entries,
      step.introHeaderFieldMapping.subtext,
      guideType
    );

    // Get the wizard step field mapping content
    const stepType = (step.wizardStepFieldMapping.stepType as WizardStepType);
    const id = step.wizardStepFieldMapping.id;
    // Make sure to mark the last step in the array as a final step as well in case
    // it's a hidden step. That way the hidden step just completes the guide
    const titleImage = findContent(
      entries,
      step.wizardStepFieldMapping.titleImage,
      guideType
    );

    // Get the primary and secondary button field mappings
    const primaryButtonText = findContent(
      entries,
      step.primaryButtonFieldMapping?.text,
      guideType
    );
    const primaryButtonVariant = step.primaryButtonFieldMapping?.variant;
    const primaryButtonRawCallback = findContent(
      entries,
      step.primaryButtonFieldMapping?.callback,
      guideType
    );
    const primaryButtonStepCallback = extractContent(
      convertToHtmlString(
        primaryButtonRawCallback?.fields.richContent, 
        userContextValues
      )
    );
    const primaryButtonType = (step.primaryButtonFieldMapping?.buttonType as WizardButtonType);

    const secondaryButtonText = findContent(
      entries,
      step.secondaryButtonFieldMapping?.text,
      guideType
    );
    const secondaryButtonVariant = step.secondaryButtonFieldMapping?.variant;
    const secondaryButtonRawCallback = findContent(
      entries,
      step.secondaryButtonFieldMapping?.callback,
      guideType
    );
    const secondaryButtonStepCallback = extractContent(
      convertToHtmlString(
        secondaryButtonRawCallback?.fields.richContent, 
        userContextValues
      )
    );
    const secondaryButtonType = (step.secondaryButtonFieldMapping?.buttonType as WizardButtonType);

    const primaryButton = {
      buttonType: primaryButtonType,
      text: convertToHtmlString(
        primaryButtonText?.fields.richContent,
        userContextValues
      ),
      variant: primaryButtonVariant,
      callback: primaryButtonStepCallback,
      isFinalStep: step.primaryButtonFieldMapping?.isFinalStep as unknown as boolean
    };

    const secondaryButton = step.secondaryButtonFieldMapping !== undefined ? {
      buttonType: secondaryButtonType,
      text: convertToHtmlString(
        secondaryButtonText?.fields.richContent,
        userContextValues
      ),
      variant: secondaryButtonVariant,
      callback: secondaryButtonStepCallback,
    } : undefined;

    // Get the content array
    const content: GuideContentWrapped[] = [];
    let minNumberContentSteps = 0;
    let maxNumberContentSteps = 0;
    (step.content as unknown as StepInnerType[])?.forEach(
      ({ type, fieldMapping: stepFieldMapping }: StepInnerType) => {
        const contentProcessed = getFactoryByType(type)(
          stepFieldMapping,
          entries,
          guideType,
          userContextValues,
        ) as GuideContent;
        if (typeof contentProcessed !== "undefined") {
          content.push({ type, content: contentProcessed });
          if (type === FactoryTypes.NUMBERED_STEP) {
            minNumberContentSteps++;
            maxNumberContentSteps++;
          } else if (type === FactoryTypes.DECISION_STEP) {
            const decisionStep = (contentProcessed as DecisionStep);
            const stepNumRange = getDecisionStepCount(decisionStep);
            minNumberContentSteps += stepNumRange[0] || 0;
            maxNumberContentSteps += stepNumRange[1] || 0;
          }
        }
      }
    );

    // Default showWizardButtons to true
    const showWizardButtons = step.wizardStepFieldMapping.showWizardButtons !== undefined 
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ? step.wizardStepFieldMapping.showWizardButtons as boolean
      : true;

    steps.push({
      introHeader: {
        title: convertToHtmlString(
          stepTitle?.fields.richContent,
          userContextValues
        ),
        subtext: convertToHtmlString(
          stepSubtitle?.fields.richContent,
          userContextValues
        ),
      },
      stepType: stepType,
      id: id,
      minNumberContentSteps: minNumberContentSteps,
      maxNumberContentSteps: maxNumberContentSteps,
      // The final step gets injected in at render time
      isFinalStep: false,
      showWizardButtons: showWizardButtons,
      titleImage: parseIntoImage(titleImage?.fields.richContent?.content[0].data),
      primaryButton: primaryButton,
      secondaryButton: secondaryButton,
      content: content
    })
  });

  return {
    steps: steps,
  };
}
